import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Edit, Building, Phone, Mail, MapPin, Users, Star, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import { ContactGrid, ContactModal, DeleteContactModal } from '../components/contacts';
import VendorCompliancePanel from '../components/vendors/VendorCompliancePanel';
import { useContacts } from '../hooks/useContacts';
import { apiService } from '../services/api';
import { formatPhoneNumber } from '../utils/formatters';
import { getComplianceStatusClasses } from '../utils/vendorCompliance';
import { useSubscription } from '../contexts/SubscriptionContext';

const VendorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendorId = parseInt(id!);
  
  // Get the source page and optional tab from URL params
  const fromPage = searchParams.get('from') || 'vendors';
  const initialTab = searchParams.get('tab') === 'compliance' ? 'compliance' : 'overview';
  const { hasFeature } = useSubscription();
  const hasVendorCompliance = hasFeature('hasVendorCompliance');
  const activeTab = hasVendorCompliance && initialTab === 'compliance' ? 'compliance' : 'overview';

  // Handle back navigation
  const handleBackNavigation = () => {
    if (fromPage === 'dashboard') {
      navigate('/dashboard');
    } else if (fromPage === 'vendors') {
      navigate('/vendors');
    } else {
      navigate(-1); // Go back to previous page
    }
  };

  // Fetch vendor details
  const {
    data: vendorResponse,
    isLoading: vendorLoading,
    error: vendorError
  } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => apiService.getVendor(vendorId),
    enabled: !!vendorId
  });



  // Use contacts hook
  const {
    contacts,
    primaryContact,
    isLoadingContacts,
    isModalOpen,
    isDeleteModalOpen,
    contactToDelete,
    handleAddNew,
    handleEdit,
    handleDelete,
    handleSetPrimary,
    handleSubmit,
    handleConfirmDelete,
    setIsModalOpen,
    setIsDeleteModalOpen
  } = useContacts({ contactType: 'vendor', parentId: vendorId });

  // Render rating stars
  const renderRating = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={`${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">{rating.toFixed(1)}</span>
      </div>
    );
  };

  // Render category badges
  const renderCategories = (categories: any[]) => {
    return (
      <div className="flex flex-wrap gap-1">
        {categories.map((category) => (
          <span
            key={category.id}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent-color-100 text-accent-color-800"
          >
            {category.name}
          </span>
        ))}
      </div>
    );
  };

  if (vendorLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (vendorError || !vendorResponse?.data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleBackNavigation}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Vendors</span>
          </Button>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center text-red-600">
            Error loading vendor details. Please try again.
          </div>
        </div>
      </div>
    );
  }

  const vendor = vendorResponse.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleBackNavigation}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Vendors</span>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{vendor.name}</h1>
              {hasVendorCompliance && vendor.complianceStatusLabel && (
                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getComplianceStatusClasses(vendor.complianceStatus, vendor.complianceStatusLabel)}`}>
                  {vendor.complianceStatusLabel}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Vendor Details</p>
          </div>
        </div>
        <Button
          onClick={() => navigate(`/vendors/${vendor.id}/edit`)}
          className="flex items-center space-x-2"
        >
          <Edit className="h-4 w-4" />
          <span>Edit Vendor</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vendor Information */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building className="h-5 w-5" />
                <span>Vendor Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500">Status</span>
                <Badge variant={vendor.isActive ? 'success' : 'secondary'}>
                  {vendor.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              {vendor.address && (
                <div className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="text-sm">
                    <div className="text-gray-900">{vendor.address}</div>
                    {(vendor.city || vendor.state || vendor.zipCode) && (
                      <div className="text-gray-600">
                        {[vendor.city, vendor.state, vendor.zipCode].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {vendor.phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{formatPhoneNumber(vendor.phone)}</span>
                </div>
              )}

              {vendor.email && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <a 
                    href={`mailto:${vendor.email}`}
                    className="text-sm text-accent-color hover:text-accent-color-800"
                  >
                    {vendor.email}
                  </a>
                </div>
              )}

              {vendor.rating && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Rating</span>
                  <div>{renderRating(vendor.rating)}</div>
                </div>
              )}

              {vendor.categories && vendor.categories.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-500 block mb-2">Categories</span>
                  {renderCategories(vendor.categories)}
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500">Created</span>
                <span className="text-sm text-gray-900">
                  {new Date(vendor.createdDate).toLocaleDateString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Primary Contact */}
          {primaryContact && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5" />
                  <span>Primary Contact</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="font-medium text-gray-900">{primaryContact.fullName}</div>
                  {primaryContact.title && (
                    <div className="text-sm text-gray-600">{primaryContact.title}</div>
                  )}
                </div>

                {primaryContact.email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <a 
                      href={`mailto:${primaryContact.email}`}
                      className="text-sm text-accent-color hover:text-accent-color-800"
                    >
                      {primaryContact.email}
                    </a>
                  </div>
                )}

                {primaryContact.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{primaryContact.phone}</span>
                  </div>
                )}

                {primaryContact.mobile && (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-accent-color">{primaryContact.mobile}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tabs: Contacts + Compliance */}
        <div className="lg:col-span-2">
          <Tabs defaultValue={activeTab}>
            <TabsList>
              <TabsTrigger value="overview">
                <Users className="h-4 w-4 mr-2 inline" />
                Contacts
              </TabsTrigger>
              {hasVendorCompliance && (
                <TabsTrigger value="compliance">
                  <Shield className="h-4 w-4 mr-2 inline" />
                  Compliance
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview" className="min-h-[24rem]">
              <ContactGrid
                contacts={contacts}
                isLoading={isLoadingContacts}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSetPrimary={handleSetPrimary}
                onAddNew={handleAddNew}
                contactType="vendor"
              />
            </TabsContent>

            {hasVendorCompliance && (
            <TabsContent value="compliance" className="min-h-[24rem]">
              <VendorCompliancePanel vendorId={vendorId} />
            </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contactType="vendor"
        parentId={vendorId}
        onSubmit={handleSubmit}
        isLoading={false}
      />

      {/* Delete Contact Modal */}
      <DeleteContactModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        contact={contactToDelete}
        contactType="vendor"
        onConfirm={handleConfirmDelete}
        isLoading={false}
      />

    </div>
  );
};

export default VendorDetailPage;
